/**
 * testerAccountService — UAT-portal standalone account client.
 *
 * Story: epic-uat-portal-001-8.
 *
 * Calls the oracle-bridge `/api/uat/auth/*` endpoints that establish a tester
 * session. These run BEFORE a session exists, so they use a plain credentialed
 * `fetch` (there is no bearer to attach yet) — `authenticatedFetch` is for the
 * tester-gated endpoints that come later (-10/-11).
 *
 * Errors are surfaced as a typed `TesterAccountApiError` carrying the HTTP
 * status and the backend's machine-readable `error` code, mirroring dao-suite's
 * `*ApiError` throw pattern so pages can branch on the code (invite_invalid /
 * email_in_use / link_invalid) rather than parsing strings.
 *
 * Backend contract (all shipped + deployed on oracle.staging.helloworlddao.com):
 *   POST /api/uat/auth/redeem       {token,email,displayName}
 *        → 200 {sessionToken, account:{id,email,displayName}}
 *        | 401 {error:'invite_invalid'} | 409 {error:'email_in_use'}
 *   POST /api/uat/auth/request-link {email} → 204 ALWAYS (no enumeration)
 *   POST /api/uat/auth/verify-link  {token}
 *        → 200 {sessionToken, account} | 401 {error:'link_invalid'}
 */

import { getOracleBridgeUrl } from '@/utils/oracleBridge';
import { authenticatedFetch } from '@/auth/authenticatedFetch';
import type { TesterAccount } from '@/auth/testerAuthContext';

/** Result of a successful redeem / verify-link: a session token + the account. */
export interface TesterSession {
  sessionToken: string;
  account: TesterAccount;
}

/**
 * Typed error for `/api/uat/auth/*` failures. `code` is the backend's
 * machine-readable `error` field (e.g. 'invite_invalid', 'email_in_use',
 * 'link_invalid') when present, else a generic fallback.
 */
export class TesterAccountApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'TesterAccountApiError';
    this.status = status;
    this.code = code;
  }
}

function authUrl(path: string): string {
  return `${getOracleBridgeUrl()}/api/uat/auth/${path}`;
}

/**
 * Parse a JSON error body WITHOUT letting a parse failure masquerade as data.
 * A non-JSON / empty body yields an empty object, never a thrown error that
 * would mask the real HTTP status (failure-as-plausible-value discipline:
 * the HTTP status is the source of truth, not a field in the body).
 */
async function readErrorCode(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body?.error === 'string' ? body.error : 'unknown_error';
  } catch {
    return 'unknown_error';
  }
}

async function readSession(response: Response): Promise<TesterSession> {
  const body = (await response.json()) as Partial<TesterSession>;
  if (
    !body ||
    typeof body.sessionToken !== 'string' ||
    !body.account ||
    typeof body.account.id !== 'string'
  ) {
    throw new TesterAccountApiError(
      'Malformed session response from the server.',
      response.status,
      'malformed_response',
    );
  }
  return { sessionToken: body.sessionToken, account: body.account };
}

/**
 * Redeem an invite token to create a lightweight tester account + open a session.
 * @throws TesterAccountApiError on 401 (invite_invalid) / 409 (email_in_use).
 */
export async function redeemInvite(
  token: string,
  email: string,
  displayName: string,
): Promise<TesterSession> {
  const response = await fetch(authUrl('redeem'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, email, displayName }),
  });

  if (!response.ok) {
    const code = await readErrorCode(response);
    const message =
      code === 'invite_invalid'
        ? 'This invite link is invalid or has expired.'
        : code === 'email_in_use'
          ? 'That email already has a tester account. Use the magic-link sign-in instead.'
          : 'Could not redeem the invite. Please try again.';
    throw new TesterAccountApiError(message, response.status, code);
  }

  return readSession(response);
}

/**
 * Request a magic sign-in link for an existing tester account. Always resolves
 * (the backend returns 204 regardless of whether the email is known — no account
 * enumeration). A non-204 success is still treated as "sent".
 * @throws TesterAccountApiError only on a hard transport/server failure.
 */
export async function requestLink(email: string): Promise<void> {
  const response = await fetch(authUrl('request-link'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  // 204 (and any 2xx) → sent. A 5xx/4xx is a real failure worth surfacing so the
  // login screen can offer a retry rather than lying "check your email".
  if (!response.ok) {
    const code = await readErrorCode(response);
    throw new TesterAccountApiError(
      'Could not send the sign-in link. Please try again.',
      response.status,
      code,
    );
  }
}

/**
 * Verify a magic-link token and open a session.
 * @throws TesterAccountApiError on 401 (link_invalid).
 */
export async function verifyLink(token: string): Promise<TesterSession> {
  const response = await fetch(authUrl('verify-link'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const code = await readErrorCode(response);
    const message =
      code === 'link_invalid'
        ? 'This sign-in link is invalid or has expired. Request a new one.'
        : 'Could not verify the sign-in link. Please try again.';
    throw new TesterAccountApiError(message, response.status, code);
  }

  return readSession(response);
}

/**
 * Revoke the current tester session on the server (epic-uat-portal-001-16).
 *
 * Posts to the tester-gated `POST /api/uat/auth/logout` via `authenticatedFetch`
 * so the current `Bearer` is attached — which is why this MUST run BEFORE
 * `clearTesterToken()`: once the token is cleared there is nothing to authorize
 * the revoke. (`authenticatedFetch` reads the token synchronously, before its
 * first `await`, so a caller that clears on the very next line is still safe.)
 *
 * FIRE-AND-FORGET: a network / transport failure is swallowed here and NEVER
 * thrown. Logout must always succeed locally even offline — the provider clears
 * the persisted session unconditionally right after calling this. The server
 * row is then also reaped by TTL + the expiry sweep, so a dropped revoke is a
 * bounded, self-healing degradation, not a stuck session.
 */
export async function logout(): Promise<void> {
  try {
    await authenticatedFetch(authUrl('logout'), { method: 'POST' });
  } catch {
    // Swallowed by design — the local clear is the client's source of truth.
  }
}
